import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Capability } from '__types__';
import { rd } from '@passionware/monads';

import useGetContactEvents from 'v1/helpers/hooks/useGetContactEvents';
import useEvent from 'v1/helpers/hooks/useEvent';

import { updateResourceSlot } from 'store/v1/productions/productions.actions.js';
import { CONTACTS_ACTIONS } from 'store/v1/contacts/contacts.constants.js';
import { PRODUCTIONS_ACTIONS } from 'store/v1/productions/productions.constants.js';
import { getStatuses } from 'store/v1/statuses/statuses.selectors.js';
import { makeGetSlotName } from 'store/v1/slot_categories/slot_categories.selectors.js';

import {
  Grid,
  GridCell,
  ListCellGroup,
  PressStud,
  ResourceAdvancedSearch,
  TextInput,
  ListCell,
  ResourceStatusActions,
  ResourceStatusGetter,
  ResourceStatusDisplay,
  ResourceTitle,
  EmptyGeneric,
  Loading
} from 'v1/components/shared';

import * as entityTypes from 'v1/helpers/entityTypes';
import { getEventStart, getEventEnd } from 'v1/helpers/byModel/EventHelper';

import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import map from 'lodash/map';

import './ProductionSidebarDrawerResourceSingle.scss';
import { SlotSearchDialogConnected } from './SlotSearchDialog.connected';
import { bestMatchesQuery } from './bestMatchesQuery';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import { myServices } from 'v5/services/services.connected.app';

const AssignedResourceRow = ({ assignment, production, slot }) => {
  const resource = rd.tryGet(
    myServices.resourceService.useItem(assignment.contact_id)
  );
  return (
    <ListCell>
      <Grid vcenter>
        <GridCell width="1/3">
          <ResourceTitle
            resource={resource ?? { id: assignment.contact_id }}
            navigateToMethod="SIDEBAR"
          />
        </GridCell>
        <GridCell>
          <ResourceStatusGetter
            assignment={assignment}
            dateRange={production.production_date}
          >
            {({ bookings }) =>
              !isEmpty(bookings) &&
              bookings.map(booking => (
                <Grid className="inset-XS" vcenter>
                  <GridCell width="1/2">
                    <ResourceStatusDisplay
                      booking={booking}
                      assignment={assignment}
                    />
                  </GridCell>
                  <GridCell width="1/2" className="text-right">
                    <ResourceStatusActions
                      booking={booking}
                      production={production}
                      resource={resource}
                      slot={slot}
                      resourceSlotAssignment={assignment}
                    />
                  </GridCell>
                </Grid>
              ))
            }
          </ResourceStatusGetter>
        </GridCell>
      </Grid>
    </ListCell>
  );
};

const ProductionSidebarDrawerResourceSingle = ({
  slot: initialSlot,
  onBackClick,
  production
}) => {
  const dispatch = useDispatch();
  const [searching, setSearching] = useState(false);

  // STORE
  const bookingStatuses = useSelector(state =>
    getStatuses(state, { entity_type: entityTypes.BOOKING })
  );
  const statuses = useSelector(state => state.statuses);
  const bookings = useSelector(state => state.bookings);
  const getSlotName = useSelector(makeGetSlotName);

  // STATE
  const [slot, setSlot] = useState(initialSlot);
  const [note, setNote] = useState(get(initialSlot, 'note', ''));

  // FUNCTION -- GETTING CONTACT EVENTS
  // This can be streamlined as it's a bit loopy
  // 1. Build resourceId array to get events for those resources
  // 2. Get dates to query between (production date)
  // 3. Send this to our useContactEvent Hook to fetch batch and bookings

  const start_date = getEventStart(get(production, 'production_date'));
  const end_date = getEventEnd(get(production, 'production_date'));

  const assignments_list = get(slot, 'resource_slot_assignments', []);
  const assignedResourceIds = assignments_list.map(a => a.contact_id);

  const assignmentContactIds = useCallback(
    () => map(assignments_list, a => a.contact_id),
    [assignments_list]
  );
  const queryWithAvailablity = useMemo(
    () => bestMatchesQuery(slot, bookingStatuses, start_date, end_date),
    [slot.query]
  );
  const bestMatches = rd.map(
    myServices.resourceService.useList(queryWithAvailablity),
    list => rowLevelValidation.validDataFromRows(list.items)
  );
  const contactResultIds = (rd.tryGet(bestMatches) ?? []).map(
    resource => resource.id
  );

  const contactEventstoFetch = assignmentContactIds().concat(contactResultIds);

  useGetContactEvents({
    contactIds: contactEventstoFetch,
    start_date,
    end_date,
    load: [contactResultIds.join(','), slot.id]
  });

  useEvent(
    [
      CONTACTS_ACTIONS.CREATE_CONTACT_EVENT,
      CONTACTS_ACTIONS.UPDATE_CONTACT_EVENT,
      PRODUCTIONS_ACTIONS.CREATE_RESOURCE_SLOT_ASSIGNMENT,
      PRODUCTIONS_ACTIONS.DELETE_RESOURCE_SLOT_ASSIGNMENT
    ],
    {
      onSuccess: () => {
        setSlot(initialSlot);
      }
    }
  );

  useEffect(() => {
    if (initialSlot && slot && initialSlot.id != slot.id) {
      setSlot(initialSlot);
      setNote(get(initialSlot, 'note', ''));
    }
  }, [initialSlot]);

  useEffect(() => {
    setSlot(initialSlot);
  }, [bookings.data]);

  const handleUpdateResourceSlotDebounced = debounce(
    handleUpdateResourceSlot,
    500
  );

  function handleUpdateResourceSlot(data) {
    setSlot({ ...slot, ...data });
    dispatch(
      updateResourceSlot(
        { resource_slot_id: slot.id, production_id: production.id },
        { ...slot, ...data }
      )
    );
  }

  function renderBestMatches(resourcesResults) {
    if (!isEmpty(resourcesResults)) {
      return resourcesResults.map(resource => (
        <ResourceStatusGetter
          key={resource.id}
          resourceId={get(resource, 'id')}
          dateRange={production.production_date}
        >
          {({ bookings = [] }) => {
            // If there is just one availability and its status is 'UNAVAILABLE'
            // then we can assume that we want to filter this out of the "Best Matches" list
            const status = statuses.data[bookings[0]?.status_id];
            if (status?.status_type !== 'UNAVAILABLE') {
              return (
                <ListCell key={`matches-${resource.id}`}>
                  <Grid vcenter>
                    <GridCell width="1/3">
                      <ResourceTitle
                        resource={resource}
                        navigateToMethod="SIDEBAR"
                      />
                    </GridCell>
                    <GridCell>
                      {!isEmpty(bookings) ? (
                        bookings.map(booking => (
                          <Grid className="inset-XS" vcenter>
                            <GridCell width="1/2">
                              <ResourceStatusDisplay booking={booking} />
                            </GridCell>
                            <GridCell width="1/2" className="text-right">
                              <ResourceStatusActions
                                booking={booking}
                                production={production}
                                slot={slot}
                                resource={resource}
                              />
                            </GridCell>
                          </Grid>
                        ))
                      ) : (
                        <Grid className="inset-XS" vcenter>
                          <GridCell className="text-right">
                            <ResourceStatusActions
                              production={production}
                              slot={slot}
                              resource={resource}
                            />
                          </GridCell>
                        </Grid>
                      )}
                    </GridCell>
                  </Grid>
                </ListCell>
              );
            } else {
              return <span />;
            }
          }}
        </ResourceStatusGetter>
      ));
    } else {
      return (
        <EmptyGeneric
          icon="/images/icon_colour_team.svg"
          title="Can't find resources with availability"
          action={{
            text: 'Search & assign resources',
            className: 'btn btn-primary btn-small',
            capability: Capability.PRODUCTION_UPDATE,
            onClick: () => setSearching(true)
          }}
        />
      );
    }
  }

  return (
    <div className="ProductionSidebarDrawerResourceSingle">
      <SlotSearchDialogConnected
        open={searching}
        onOpenChange={setSearching}
        production={production}
        slot={slot}
        slotName={getSlotName(slot)}
      />
      <Grid gutters="S" vcenter className="stack-S">
        <GridCell width="auto">
          <PressStud icon="arrowLeftGray" action={onBackClick} />
        </GridCell>
        <GridCell width="auto">
          <h3>{getSlotName(slot)}</h3>
        </GridCell>
        <GridCell>
          <ResourceAdvancedSearch
            slimline={true}
            showMeta={true}
            query={slot.query || {}}
            onChange={query => {
              handleUpdateResourceSlot({ query });
            }}
          />
        </GridCell>
      </Grid>
      <p className="text-11-700 fix-margin">Note</p>
      <TextInput
        value={note}
        onBlur={value => {
          setNote(value);
          handleUpdateResourceSlotDebounced({ note: value });
        }}
        // onChange={({ target }) => {
        // }}
        placeholder="Add a note..."
      />
      <p className="text-11-700">
        ASSIGNED TO THIS PRODUCTION ({assignments_list.length})
      </p>
      {!isEmpty(assignments_list) ? (
        <ListCellGroup className="stack-S">
          {assignments_list.length &&
            assignments_list.map(assignment => (
              <AssignedResourceRow
                key={`assigned-${assignment.contact_id}`}
                assignment={assignment}
                production={production}
                slot={slot}
              />
            ))}
        </ListCellGroup>
      ) : (
        <div className="ProductionSidebarDrawerResourceSingle-emptyResources inset-M stack-M text-center">
          No resources assigned
        </div>
      )}
      {!isEmpty(slot.query) ? (
        <>
          <Grid vcenter className="stack-S">
            <GridCell>
              <span className="text-11-700">BEST MATCHES</span>
            </GridCell>
            <GridCell width="auto">
              <PressStud
                label="Search all resources"
                icon="search"
                size="S"
                action={() => setSearching(true)}
              />
            </GridCell>
          </Grid>
          {rd.isSuccess(bestMatches) ? (
            <ListCellGroup>
              {renderBestMatches(
                bestMatches.data.filter(
                  resource => !assignedResourceIds.includes(resource.id)
                )
              )}
            </ListCellGroup>
          ) : (
            <Loading />
          )}
        </>
      ) : (
        <EmptyGeneric
          icon="/images/icon_colour_team.svg"
          title="Assign resource to this role"
          action={{
            text: 'Search & assign resources',
            className: 'btn btn-primary btn-small',
            capability: Capability.PRODUCTION_UPDATE,
            onClick: () => setSearching(true)
          }}
        />
      )}
    </div>
  );
};

ProductionSidebarDrawerResourceSingle.propTypes = {
  slot: PropTypes.object, // One item from production.resource_slots
  onBackClick: PropTypes.func
};

export default ProductionSidebarDrawerResourceSingle;
